import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Tag,
} from '@chakra-ui/react';
import { US_STATES } from '@common/constants';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { EmploymentInformationValues } from '@common/models/InvestorAccountModel/types';
import { EmploymentStatus } from '@services/appsync/types';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

export const EmploymentForm: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>();
  const [needsAttention, setNeedsAttention] = useState<boolean>(false);
  const { investorAccount, updateEmploymentInformation } = useInvestorAccountModel();
  const survey = investorAccount?.non_accredited_survey_v2 ?? null;

  const form = useForm<EmploymentInformationValues>({
    defaultValues: {
      brokerDealerAssociation: survey?.broker_dealer_assoc?.value ?? null,
      employer: investorAccount?.employment?.employer ?? null,
      employerAddress: investorAccount?.employment?.employer_address ?? null,
      employerAddress2: investorAccount?.employment?.employer_address2 ?? null,
      employerCity: investorAccount?.employment?.employer_city ?? null,
      employerState: investorAccount?.employment?.employer_state ?? null,
      employerZip: investorAccount?.employment?.employer_zip ?? null,
      employmentStatus: investorAccount?.employment?.employment_status ?? EmploymentStatus.EMPLOYED,
      occupation: investorAccount?.employment?.occupation ?? null,
    },
  });

  const save = form.handleSubmit(async (data) => {
    setIsSaving(true);
    setFormError(null);
    const result = await updateEmploymentInformation(data);
    if (result.error) {
      form.reset();
      setIsSaving(false);
      setFormError(result.error);
    } else {
      form.reset(data);
      setIsSaving(false);
    }
  });

  useEffect(() => {
    const needsToSpecifyBrokereDealerAssociation = survey?.broker_dealer_assoc?.value === undefined;
    const needsToSpecifyEmploymentStatus = investorAccount?.employment?.employment_status === undefined;
    const needsToProvideEmployerInformation =
      investorAccount?.employment?.employment_status === EmploymentStatus.EMPLOYED
        ? investorAccount?.employment?.employer === undefined ||
          investorAccount?.employment?.employer_address === undefined ||
          investorAccount?.employment?.employer_city === undefined ||
          investorAccount?.employment?.employer_state === undefined ||
          investorAccount?.employment?.employer_zip === undefined
        : false;
    setNeedsAttention(
      needsToSpecifyBrokereDealerAssociation || needsToSpecifyEmploymentStatus || needsToProvideEmployerInformation,
    );
  }, [survey]);

  const employmentStatusWatch = useWatch({ control: form.control, name: 'employmentStatus' });

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="employment" fontSize={'21px'}>
            Employment
          </Heading>
          {needsAttention && <Tag colorScheme={'yellow'}>Needs Attention</Tag>}
        </Stack>
        {form.formState.isDirty &&
          (isSaving ? (
            <Spinner color="blue.500" size="md" thickness="4px" />
          ) : (
            <HStack spacing={5}>
              <Button onClick={save}>Save</Button>
              <Button variant="link" onClick={() => form.reset()}>
                Cancel
              </Button>
            </HStack>
          ))}
      </GridItem>
      {formError && (
        <GridItem colSpan={2}>
          <Alert status="error">
            <AlertIcon />
            {formError}
          </Alert>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Dealer Broker */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="brokerDealerAssociation">Are you employed by a broker-dealer?</FormLabel>
          <FormHelperText>
            For most people, the answer is "No". You might answer "yes" if you or an immediate family member works for a
            company that trades securities on behalf of clients.
          </FormHelperText>
        </FormControl>
      </GridItem>

      <GridItem colSpan={[2, 1]}>
        <FormControl isInvalid={Boolean(form.formState.errors.brokerDealerAssociation)}>
          <Select
            isDisabled={isSaving}
            placeholder="Select option"
            {...form.register('brokerDealerAssociation', {
              validate: (value) => {
                if (!value) {
                  return 'Broker-dealer association is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value="true">Yes</option>
            <option value="false" defaultValue="true">
              No
            </option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.brokerDealerAssociation && form.formState.errors.brokerDealerAssociation?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Employment Status */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="employmentStatus">What's your employment status?</FormLabel>
        </FormControl>
      </GridItem>
      <GridItem colSpan={[2, 1]}>
        <FormControl isInvalid={Boolean(form.formState.errors.employmentStatus)}>
          <Select
            isDisabled={isSaving}
            placeholder="Select option"
            defaultValue={investorAccount?.employment?.employment_status ?? EmploymentStatus.EMPLOYED}
            {...form.register('employmentStatus', {
              onChange: (v) => {
                if (v.target.value !== EmploymentStatus.EMPLOYED) {
                  form.setValue('employer', null);
                  form.setValue('employerAddress', null);
                  form.setValue('employerAddress2', null);
                  form.setValue('employerCity', null);
                  form.setValue('employerState', null);
                  form.setValue('employerZip', null);
                  form.setValue('occupation', null);
                  form.clearErrors();
                }
                return v;
              },
              validate: (value) => {
                if (!value) {
                  return 'Employment status is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={EmploymentStatus.EMPLOYED}>Employed</option>
            <option value={EmploymentStatus.RETIRED}>Retired</option>
            <option value={EmploymentStatus.STUDENT}>Student</option>
            <option value={EmploymentStatus.UNEMPLOYED}>Unemployed</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.employmentStatus && form.formState.errors.employmentStatus?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      {/* Occupation */}
      <GridItem colSpan={[2, 1]}>
        <FormControl>
          <FormLabel htmlFor="stockExperience">What's your occupation?</FormLabel>
        </FormControl>
      </GridItem>

      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.occupation)}>
          <Input
            id="occupation"
            placeholder="Job title"
            {...form.register('occupation', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'Occupation is required';
                } else {
                  return true;
                }
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.occupation && form.formState.errors.occupation?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      <GridItem
        colSpan={2}
        borderTopWidth={1}
        borderTopColor={'gray.50'}
        pt={'20px'}
        opacity={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving ? 0.5 : 1}>
        <TextBlock fontSize="lg">Employer Information</TextBlock>
      </GridItem>

      {/* Employer Name */}
      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.employer)}>
          <FormLabel htmlFor="stockExperience">Name</FormLabel>
          <Input
            id="employer"
            placeholder="Name of your employer"
            {...form.register('employer', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'Employer name is required';
                } else {
                  return true;
                }
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.employer && form.formState.errors.employer?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Employer Address */}
      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.employerAddress)}>
          <FormLabel htmlFor="stockExperience">Address 1</FormLabel>
          <Input
            id="employerAddress"
            placeholder="Address of your employer"
            {...form.register('employerAddress', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'Address is required';
                } else {
                  return true;
                }
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.employerAddress && form.formState.errors.employerAddress?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Employer Address  2*/}
      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}>
          <FormLabel htmlFor="stockExperience">Address 2</FormLabel>
          <Input id="employerAddress2" placeholder="" {...form.register('employerAddress2')} />
        </FormControl>
      </GridItem>

      {/* Employer City */}
      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.employerCity)}>
          <FormLabel htmlFor="stockExperience">City</FormLabel>
          <Input
            id="employerCity"
            placeholder="City of your employer"
            {...form.register('employerCity', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'City is required';
                } else {
                  return true;
                }
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.employerCity && form.formState.errors.employerCity?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Employer State */}
      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.employerState)}>
          <FormLabel htmlFor="state">State</FormLabel>
          <Select
            mb={3}
            placeholder="Select option"
            defaultValue={investorAccount?.employment?.employer_state || ''}
            {...form.register('employerState', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'State is required';
                } else {
                  return true;
                }
              },
            })}>
            {Object.keys(US_STATES).map((key) => {
              return (
                <option key={`state-${key}`} value={key}>
                  {US_STATES[key]}
                </option>
              );
            })}
          </Select>
          <FormErrorMessage>
            {form.formState.errors.employerState && form.formState.errors.employerState.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Employer Zip */}
      <GridItem colSpan={[2, 1]}>
        <FormControl
          isDisabled={employmentStatusWatch !== EmploymentStatus.EMPLOYED || isSaving}
          isInvalid={Boolean(form.formState.errors.employerZip)}>
          <FormLabel htmlFor="stockExperience">Zip</FormLabel>
          <Input
            id="employerZip"
            placeholder="Zip of your employer"
            {...form.register('employerZip', {
              validate: (value) => {
                if (!value && employmentStatusWatch === EmploymentStatus.EMPLOYED) {
                  return 'Zip is required';
                } else {
                  return true;
                }
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.employerZip && form.formState.errors.employerZip?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
